import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react/macro';
import { t } from '@lingui/macro';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { InlineConfirm } from '@/components/ui/confirm-dialog';
import { Loader2 } from 'lucide-react';
import type { TariffVersion } from '@/types/billing';
import {
  getTariffVersionSchema,
  type TariffVersionFormData,
  type TariffVersionFormInput,
  TariffStatusEnum,
  type TariffStatus
} from '@/schemas/tariffValidationSchemas';
import { TariffBasicInfoSection } from './TariffBasicInfoSection';
import { resolveInheritedAgeTransition, extractAgeTransition } from './ageTransitionInheritance';
import { resolveInheritedRounding, extractRounding } from './roundingSettingsInheritance';

interface GroupTypeOption {
  value: string;
  label: string;
}

interface TariffVersionFormProps {
  initialData?: TariffVersion;
  onSubmit: (data: TariffVersionFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  isCopy?: boolean;
  isEdit?: boolean;
  // `form.getValues()` yields the form's INPUT shape (defaulted keys may still be
  // absent), so the activate path — which does not go through the resolver —
  // hands on the input type, not the validated output type.
  onActivate?: (formData: TariffVersionFormInput) => Promise<void>;
  isActivating?: boolean;
  canActivate?: boolean;
  onCopy?: () => void;
  canCopy?: boolean;
  /** May return a promise: the inline confirm stays open until it resolves. */
  onArchive?: () => Promise<unknown> | void;
  isArchiving?: boolean;
  canArchive?: boolean;
  onEditRates?: () => void;
  canEditRates?: boolean;
  groupTypeOptions?: GroupTypeOption[];
  isLoadingEnums?: boolean;
  // All known tariff versions, used to prefill the age-transition fields on
  // create from the most-recent ACTIVE version of the selected group type.
  versions?: TariffVersion[];
}

export function TariffVersionForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isCopy = false,
  isEdit = false,
  onActivate,
  isActivating = false,
  canActivate = false,
  onCopy,
  canCopy = false,
  onArchive,
  isArchiving = false,
  canArchive = false,
  onEditRates,
  canEditRates = false,
  groupTypeOptions,
  isLoadingEnums = false,
  versions,
}: TariffVersionFormProps) {
  const { i18n } = useLingui();

  // Pass available group types to schema for validation
  const availableGroupTypes = groupTypeOptions?.map(opt => opt.value);
  const schema = getTariffVersionSchema(i18n, availableGroupTypes);

  const initialGroupType = initialData?.group_type || initialData?.groupType || 'STANDARD';

  // For copy/edit, prefill the age-transition fields from the source/edited
  // version; for create, inherit from the most-recent ACTIVE same-group-type
  // version (else canonical defaults).
  const initialAgeTransition =
    (isCopy || isEdit) && initialData
      ? extractAgeTransition(initialData)
      : resolveInheritedAgeTransition(versions, initialGroupType);

  // Same inherit-on-create / extract-on-copy-edit rule for the rounding settings.
  const initialRounding =
    (isCopy || isEdit) && initialData
      ? extractRounding(initialData)
      : resolveInheritedRounding(versions, initialGroupType);

  const form = useForm<TariffVersionFormInput, unknown, TariffVersionFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: isCopy ? `${initialData?.name || ''} (Copy)` : initialData?.name || '',
      description: initialData?.description || '',
      group_type: initialGroupType,
      status: isCopy
        ? TariffStatusEnum.DRAFT
        : (initialData?.status as TariffStatus) || TariffStatusEnum.DRAFT,
      is_default: isCopy ? false : (initialData?.is_default || initialData?.isDefault || false),
      age_transition_months: initialAgeTransition.months,
      age_transition_months_subsidised: initialAgeTransition.monthsSubsidised,
      age_transition_timing: initialAgeTransition.timing,
      rounding_parents_enabled: initialRounding.parentsEnabled,
      rounding_parents_amount: initialRounding.parentsAmount,
      rounding_subsidy_enabled: initialRounding.subsidyEnabled,
      rounding_subsidy_amount: initialRounding.subsidyAmount,
    },
  });

  // On create, re-prefill the age-transition fields from the most-recent ACTIVE
  // version when the inherited *source* changes — i.e. the selected group type
  // switches, or `versions` transitions from not-yet-loaded to loaded. A bare
  // `versions` refetch (same load state, new array reference) must NOT re-run the
  // prefill, otherwise it would clobber the user's manual edits to these fields.
  const isCreateMode = !isEdit && !isCopy;
  const watchedGroupType = form.watch('group_type');
  const prefillKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isCreateMode) return;
    const prefillKey = `${watchedGroupType}|${versions && versions.length > 0 ? 'loaded' : 'empty'}`;
    if (prefillKeyRef.current === prefillKey) return;
    prefillKeyRef.current = prefillKey;
    const inherited = resolveInheritedAgeTransition(versions, watchedGroupType);
    form.setValue('age_transition_months', inherited.months);
    form.setValue('age_transition_months_subsidised', inherited.monthsSubsidised);
    form.setValue('age_transition_timing', inherited.timing);
    const inheritedRounding = resolveInheritedRounding(versions, watchedGroupType);
    form.setValue('rounding_parents_enabled', inheritedRounding.parentsEnabled);
    form.setValue('rounding_parents_amount', inheritedRounding.parentsAmount);
    form.setValue('rounding_subsidy_enabled', inheritedRounding.subsidyEnabled);
    form.setValue('rounding_subsidy_amount', inheritedRounding.subsidyAmount);
  }, [isCreateMode, watchedGroupType, versions, form]);

  const handleSubmit = async (data: TariffVersionFormData) => {
    await onSubmit(data);
  };

  const handleActivate = async () => {
    if (!onActivate) return;

    // Get current form values to pass to activate handler
    const formData = form.getValues();
    await onActivate(formData);
  };

  const isEditMode = isEdit;
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <TariffBasicInfoSection 
          form={form}
          isLoading={isLoading || isLoadingEnums}
          isEditMode={isEditMode}
          groupTypeOptions={groupTypeOptions}
        />

        {confirmingArchive && onArchive ? (
          <InlineConfirm
            message={i18n._(t({ id: "tariffs.archive.confirm", message: "Archive this tariff version?" }))}
            confirmLabel={i18n._(t({ id: "common.archive", message: "Archive" }))}
            onConfirm={onArchive}
            onCancel={() => setConfirmingArchive(false)}
            isPending={isArchiving}
          />
        ) : (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {/* Left side - Set as Default, Copy, Archive buttons */}
            {isEdit && canActivate && onActivate && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleActivate}
                disabled={isActivating || isLoading || isArchiving}
              >
                {isActivating
                  ? i18n._(t`Setting as Default...`)
                  : i18n._(t({ id: "tariffs.actions.setAsDefault", message: "Set as Default" }))}
              </Button>
            )}
            {isEdit && canCopy && onCopy && (
              <Button
                type="button"
                variant="outline"
                onClick={onCopy}
                disabled={isLoading || isActivating || isArchiving}
              >
                {i18n._(t({ id: "common.copy", message: "Copy" }))}
              </Button>
            )}
            {isEdit && canArchive && onArchive && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setConfirmingArchive(true)}
                disabled={isLoading || isActivating || isArchiving}
              >
                {i18n._(t({ id: "common.archive", message: "Archive" }))}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* Loading message */}
            {isLoading && !isEditMode && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {i18n._(t({ id: "tariffs.creating", message: "Creating tariffs..." }))}
              </div>
            )}
            {isLoading && isEditMode && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {i18n._(t({ id: "tariffs.updating", message: "Updating tariff..." }))}
              </div>
            )}
            {/* Edit Rates, Cancel and Submit buttons */}
            <div className="flex gap-3">
              {isEdit && canEditRates && onEditRates && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onEditRates}
                  disabled={isLoading || isActivating || isArchiving}
                >
                  {i18n._(t({ id: "tariffs.actions.editRates", message: "Edit Rates" }))}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isLoading || isActivating}
              >
                {i18n._(t({ id: "common.cancel", message: "Cancel" }))}
              </Button>
              <Button type="submit" disabled={isLoading || isActivating}>
                {isLoading && !isEditMode ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    {i18n._(t({ id: "tariffs.creating.button", message: "Creating..." }))}
                  </>
                ) : isLoading && isEditMode ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    {i18n._(t({ id: "tariffs.updating.button", message: "Updating..." }))}
                  </>
                ) : isEditMode ? (
                  i18n._(t({ id: "tariffs.actions.updateVersion", message: "Update Version" }))
                ) : (
                  i18n._(t({ id: "tariffs.actions.createVersion", message: "Create Version" }))
                )}
              </Button>
            </div>
          </div>
        </div>
        )}
      </form>
    </Form>
  );
}